import { muiGlyph } from "@/components/ui/mui-glyph";

// The glyphs the communities list and details pages draw outside their maps,
// each path copied byte-for-byte from `@mui/icons-material` 9.2.0 (`<Name>.js`):
// the row menu's Hide / Unhide (`VisibilityOff` / `Visibility`) and Create
// Specification (`NoteAddOutlined`), the expansion panel's open chevron
// (`ExpandMore` — its closed one is `mui-spec-icons.tsx`'s `ChevronRight`), the
// drawer's fold (`ExpandLess`) and back arrow (`ArrowBack`), the map legend's
// homesite mark (`OtherHousesOutlined`), the Create Visualization CTA's camera
// (`CameraIndoorOutlined`), and Add Link (`Add`).
//
// Declared at `SvgIcon`'s medium 24px box (`SvgIcon.js:75-78`); a slot that
// draws one smaller — a menu item's, a small button's — resizes it by
// `className`, as MUI's slot rules resize an `SvgIcon`.

/** `Visibility.js` — Unhide community. */
const VISIBILITY_PATH =
  "M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5M12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5m0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3";
/** `VisibilityOff.js` — Hide community. */
const VISIBILITY_OFF_PATH =
  "M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7M2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2m4.31-.78 3.15 3.15.02-.16c0-1.66-1.34-3-3-3z";
/** `NoteAddOutlined.js` — Create Specification. */
const NOTE_ADD_OUTLINED_PATH =
  "M13 11h-2v3H8v2h3v3h2v-3h3v-2h-3zm1-9H6c-1.1 0-2 .9-2 2v16c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8zm4 18H6V4h7v5h5z";
/** `ExpandMore.js` / `ExpandLess.js`. */
const EXPAND_MORE_PATH = "M16.59 8.59 12 13.17 7.41 8.59 6 10l6 6 6-6z";
const EXPAND_LESS_PATH = "m12 8-6 6 1.41 1.41L12 10.83l4.59 4.58L18 14z";
/** `OtherHousesOutlined.js`. */
const OTHER_HOUSES_OUTLINED_PATH =
  "M12 3 1 11.4l1.21 1.59L4 11.62V21h16v-9.38l1.79 1.36L23 11.4zm6 16H6v-8.9l6-4.58 6 4.58zm-9-5c0 .55-.45 1-1 1s-1-.45-1-1 .45-1 1-1 1 .45 1 1m3-1c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1m3 1c0-.55.45-1 1-1s1 .45 1 1-.45 1-1 1-1-.45-1-1";
/** `CameraIndoorOutlined.js` — "Build Visualization Specs!". */
const CAMERA_INDOOR_OUTLINED_PATH =
  "M14 13v-1c0-.55-.45-1-1-1H9c-.55 0-1 .45-1 1v4c0 .55.45 1 1 1h4c.55 0 1-.45 1-1v-1l2 1.06v-4.12zm-2-7.5 6 4.5v9H6v-9zM12 3 4 9v12h16V9z";
/** `ArrowBack.js`. */
const ARROW_BACK_PATH = "M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20z";
/** `Add.js` — Add Link. (`lookup-select-field.tsx` inlines the same path.) */
const ADD_PATH = "M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6z";

export const MuiVisibilityIcon = muiGlyph("mui-visibility", VISIBILITY_PATH, "size-6");
export const MuiVisibilityOffIcon = muiGlyph("mui-visibility-off", VISIBILITY_OFF_PATH, "size-6");
export const MuiNoteAddOutlinedIcon = muiGlyph(
  "mui-note-add-outlined",
  NOTE_ADD_OUTLINED_PATH,
  "size-6",
);
export const MuiExpandMoreIcon = muiGlyph("mui-expand-more", EXPAND_MORE_PATH, "size-6");
export const MuiExpandLessIcon = muiGlyph("mui-expand-less", EXPAND_LESS_PATH, "size-6");
export const MuiOtherHousesOutlinedIcon = muiGlyph(
  "mui-other-houses-outlined",
  OTHER_HOUSES_OUTLINED_PATH,
  "size-6",
);
export const MuiCameraIndoorOutlinedIcon = muiGlyph(
  "mui-camera-indoor-outlined",
  CAMERA_INDOOR_OUTLINED_PATH,
  "size-6",
);
export const MuiArrowBackIcon = muiGlyph("mui-arrow-back", ARROW_BACK_PATH, "size-6");
export const MuiAddIcon = muiGlyph("mui-add", ADD_PATH, "size-6");
